import { guessClientIdFromMessage } from "./bluNotionIntent";

const SCOPE_ITEM_LINE = /^(?:Section\s+)?(\d{1,2})\s*(?:—|–|-|:)\s*(.+)$/i;
const SCOPE_ITEM_SPLIT = /(?=^(?:Section\s+)?\d{1,2}\s*[—–-])/im;

function isPlaceholderClientId(id) {
  const s = String(id || "").trim();
  if (!s) return true;
  if (/^\[.+\]$/.test(s)) return true;
  if (/portal client id/i.test(s)) return true;
  return false;
}

function looksLikeFinalizePayload(obj) {
  return (
    obj &&
    typeof obj === "object" &&
    Array.isArray(obj.scope_items) &&
    String(obj.client_name || "").trim()
  );
}

function resolveFinalizeClientId(parsed, clients) {
  const name = String(parsed?.client_name || "").trim();
  let id = String(parsed?.client_id || "").trim();
  if (id && !isPlaceholderClientId(id)) return id;
  if (name && Array.isArray(clients) && clients.length) {
    return guessClientIdFromMessage(name, clients);
  }
  return isPlaceholderClientId(id) ? "" : id;
}

function parseFinalizeJsonString(raw) {
  try {
    const parsed = JSON.parse(String(raw).trim());
    return looksLikeFinalizePayload(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function tryParseFinalizeObject(raw, clients) {
  const parsed = parseFinalizeJsonString(raw);
  if (!parsed) return null;
  const client_id = resolveFinalizeClientId(parsed, clients);
  if (!client_id) return null;
  return { ...parsed, client_id, client_name: String(parsed.client_name || "").trim() };
}

/** Pull all valid finalize JSON strings from blu-finalize / json fences (order preserved). */
export function extractAllBluFinalizeJson(text) {
  const source = String(text || "");
  if (!source.trim()) return [];

  const found = [];
  const seen = new Set();
  const fencePatterns = [
    /```blu-finalize\s*([\s\S]*?)```/gi,
    /```json\s*([\s\S]*?)```/gi,
  ];

  for (const pattern of fencePatterns) {
    pattern.lastIndex = 0;
    let match;
    while ((match = pattern.exec(source))) {
      const raw = (match[1] || "").trim();
      if (!parseFinalizeJsonString(raw)) continue;
      if (seen.has(raw)) continue;
      seen.add(raw);
      found.push(raw);
    }
  }

  if (found.length) return found;

  // Single trailing / unfenced object fallback (same as extractBluFinalizeJson).
  const unclosed = source.match(/```(?:blu-finalize|json)?\s*([\s\S]+)$/i);
  if (unclosed) {
    const body = unclosed[1].replace(/```\s*$/, "").trim();
    if (parseFinalizeJsonString(body)) return [body];
  }

  const start = source.indexOf("{");
  if (start >= 0) {
    for (let end = source.length; end > start; end -= 1) {
      const slice = source.slice(start, end).trim();
      if (!slice.endsWith("}")) continue;
      if (parseFinalizeJsonString(slice)) return [slice];
    }
  }

  return [];
}

/** Pull JSON from fenced blocks or trailing object literals. */
export function extractBluFinalizeJson(text) {
  const all = extractAllBluFinalizeJson(text);
  return all[0] || null;
}

export function parseBluFinalize(content, { clients } = {}) {
  const json = extractBluFinalizeJson(content);
  if (!json) return null;
  return tryParseFinalizeObject(json, clients);
}

/** Parse every blu-finalize block in a message (for shareable option packets). */
export function parseAllBluFinalizes(content, { clients } = {}) {
  return extractAllBluFinalizeJson(content)
    .map((json) => tryParseFinalizeObject(json, clients))
    .filter(Boolean);
}

function stripMarkdownInline(text) {
  return String(text || "")
    .replace(/^\*\*|\*\*$/g, "")
    .replace(/\*\*/g, "")
    .trim();
}

function parseMoney(text) {
  const m = String(text || "").match(/\$[\d,]+(?:\.\d{2})?/);
  if (!m) return 0;
  return Number(m[0].replace(/[$,]/g, "")) || 0;
}

/** Build a finalize payload from Step 1 markdown when BLU omits the JSON block. */
export function parseBluScopeBriefMarkdown(content, { clients } = {}) {
  const text = String(content || "");
  if (!/(?:^|\n)\s*#{1,2}\s*SCOPE OF SUPPORT/i.test(text)) return null;

  const afterHeading = text.split(/#{1,2}\s*SCOPE OF SUPPORT/i)[1];
  if (!afterHeading) return null;

  const section = afterHeading.trim();
  const lines = section.split(/\r?\n/).map((l) => l.trim());

  let client_name = "";
  let date_range = "";
  for (let i = 0; i < lines.length; i += 1) {
    const line = stripMarkdownInline(lines[i]);
    if (!line || line.startsWith("---")) continue;
    if (/^(direction|what we'?re scoping|investment|scope)\b/i.test(line)) break;
    if (/^\d{1,2}\s*[—–-]/i.test(line) || /^Section\s+\d/i.test(line)) break;
    if (!client_name) {
      client_name = line;
      continue;
    }
    if (!date_range) {
      date_range = line;
      break;
    }
  }

  let intro = "";
  const directionMatch = section.match(
    /\*\*DIRECTION\*\*\s*\n+([\s\S]*?)(?=\n\s*\*\*|\n---|\n\*\*WHAT|\n\*\*INVESTMENT|$)/i,
  );
  if (directionMatch) {
    intro = directionMatch[1].trim().split(/\n\n+/)[0].replace(/\*\*/g, "").trim();
  }

  const scope_items = [];
  const scopingMatch = section.match(
    /\*\*WHAT WE'?RE SCOPING\*\*\s*\n+([\s\S]*?)(?=\n\s*\*\*INVESTMENT|\n---|$)/i,
  );
  if (scopingMatch) {
    const block = scopingMatch[1].trim();
    const chunks = block.split(SCOPE_ITEM_SPLIT).map((c) => c.trim()).filter(Boolean);
    for (const chunk of chunks) {
      const linesChunk = chunk.split(/\r?\n/);
      const head = linesChunk[0] || "";
      const m = head.match(SCOPE_ITEM_LINE);
      if (!m) continue;
      const description = linesChunk.slice(1).join("\n").trim();
      scope_items.push({
        number: String(m[1]).padStart(2, "0"),
        title: m[2].trim(),
        description,
      });
    }
  }

  if (scope_items.length === 0) {
    const fallbackBlock = section.replace(/\*\*INVESTMENT\*\*[\s\S]*/i, "").trim();
    const chunks = fallbackBlock.split(SCOPE_ITEM_SPLIT).map((c) => c.trim()).filter(Boolean);
    for (const chunk of chunks) {
      const linesChunk = chunk.split(/\r?\n/);
      const head = linesChunk[0] || "";
      const m = head.match(SCOPE_ITEM_LINE);
      if (!m) continue;
      const description = linesChunk.slice(1).join("\n").trim();
      scope_items.push({
        number: String(m[1]).padStart(2, "0"),
        title: m[2].trim(),
        description,
      });
    }
  }

  if (!client_name || scope_items.length === 0) return null;

  const client_id = resolveFinalizeClientId({ client_id: "", client_name }, clients);
  if (!client_id) return null;

  let investment_total = 0;
  const invMatch = section.match(/\*\*INVESTMENT\*\*\s*\n+([\s\S]*?)(?=\n---|$)/i);
  if (invMatch) investment_total = parseMoney(invMatch[1]);

  const today = new Date();
  const issued = today.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  return {
    client_id,
    client_name,
    date_range,
    intro,
    scope_items,
    investment_total,
    invoice_service_title: "OPERATIONAL ECOSYSTEM",
    invoice_service_description: "",
    subtotal: investment_total,
    credits: 0,
    credit_label: "Referral Credit",
    total: investment_total,
    issued_date: issued,
    due_date: issued,
    notes: null,
  };
}

/** Parse finalize JSON from a message, or rebuild from the nearest prior scope brief. */
export function resolveBluDocPayload(content, { clients, messages, messageIndex } = {}) {
  const direct = parseBluFinalize(content, { clients });
  if (direct) return direct;

  if (messageIndex == null || !Array.isArray(messages)) return null;

  for (let j = messageIndex - 1; j >= 0; j -= 1) {
    const msg = messages[j];
    if (msg?.role !== "assistant") continue;
    const fromBrief = parseBluScopeBriefMarkdown(msg.content, { clients });
    if (fromBrief) return fromBrief;
    if (parseBluFinalize(msg.content, { clients })) break;
  }

  return null;
}

function toStr(v) {
  return v == null ? "" : String(v);
}

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Coerce BLU model output so FastAPI BluFinalizeRequest validation usually passes. */
export function normalizeBluFinalizePayload(raw) {
  if (!raw || typeof raw !== "object") return null;
  const items = Array.isArray(raw.scope_items) ? raw.scope_items : [];
  const scope_items = items.map((it, idx) => {
    const num = toStr(it?.number).trim();
    return {
      number: num || String(idx + 1).padStart(2, "0"),
      title: toStr(it?.title),
      description: toStr(it?.description),
    };
  });
  const option_label = toStr(raw.option_label).trim();
  const exchange = toStr(raw.exchange || raw.they_provide).trim();
  const scope_only = Boolean(raw.scope_only) || Boolean(exchange);
  return {
    ...raw,
    client_id: toStr(raw.client_id).trim(),
    client_name: toStr(raw.client_name).trim(),
    date_range: toStr(raw.date_range),
    intro: toStr(raw.intro),
    scope_items,
    scope_only,
    exchange: exchange || null,
    investment_total: toNum(raw.investment_total),
    invoice_service_title: toStr(raw.invoice_service_title || "OPERATIONAL ECOSYSTEM") || "OPERATIONAL ECOSYSTEM",
    invoice_service_description: toStr(raw.invoice_service_description),
    subtotal: toNum(raw.subtotal),
    credits: toNum(raw.credits),
    credit_label: toStr(raw.credit_label || "Referral Credit") || "Referral Credit",
    total: toNum(raw.total),
    issued_date: toStr(raw.issued_date),
    due_date: toStr(raw.due_date),
    notes: raw.notes == null || raw.notes === "" ? null : toStr(raw.notes),
    option_label: option_label || null,
  };
}

export function stripBluFinalizeBlock(content) {
  let out = String(content || "");
  out = out.replace(/```blu-finalize[\s\S]*?```/gi, "");
  out = out.replace(/```json\s*([\s\S]*?)```/gi, (full, inner) =>
    parseFinalizeJsonString(inner) ? "" : full,
  );
  return out.trim();
}

/** True when assistant message is a multi-package SCOPE OPTIONS menu (not a single Step 1). */
export function isBluScopeOptionsMessage(content) {
  const text = String(content || "");
  if (!/(?:^|\n)\s*#{1,2}\s*SCOPE OPTIONS\b/i.test(text)) return false;
  // Prefer options menu over a combined message that also has SCOPE OF SUPPORT.
  return true;
}

/**
 * Parse ## SCOPE OPTIONS packages for Build chips.
 * Returns { client_name, options: [{ id, label, shortLabel, investment }] } or null.
 */
export function parseBluScopeOptionsMarkdown(content, { clients } = {}) {
  const text = String(content || "");
  if (!/(?:^|\n)\s*#{1,2}\s*SCOPE OPTIONS\b/i.test(text)) return null;

  const after = text.split(/#{1,2}\s*SCOPE OPTIONS\b/i)[1];
  if (!after) return null;

  // Stop before a full Step 1 if somehow combined.
  const section = after.split(/#{1,2}\s*SCOPE OF SUPPORT\b/i)[0];

  let client_name = "";
  const nameMatch = section.match(
    /(?:^|\n)\s*(?:\*\*)?(?:Client|Prospect)\s*:\s*(.+?)(?:\*\*)?\s*(?:\n|$)/i,
  );
  if (nameMatch) {
    client_name = stripMarkdownInline(nameMatch[1]);
  }
  if (!client_name && Array.isArray(clients)) {
    const lower = section.toLowerCase();
    let best = null;
    for (const c of clients) {
      const n = (c?.name || "").trim();
      if (n.length < 2) continue;
      if (lower.includes(n.toLowerCase()) && (!best || n.length > best.length)) best = n;
    }
    client_name = best || "";
  }

  const options = [];
  // ### Option A · Essential — $12,000  OR  **Option B · Core — $18,000**
  const headingRe =
    /(?:^|\n)\s*(?:#{2,3}\s*|\*\*)\s*Option\s+([A-Z0-9]+)\s*[·•\-–—|:]\s*([^*\n]+?)(?:\*\*)?\s*(?=\n|$)/gi;
  let m;
  while ((m = headingRe.exec(section)) !== null) {
    const letter = String(m[1] || "").trim();
    const rest = stripMarkdownInline(m[2] || "").trim();
    // rest may be "Essential — $12,000" or "Core — $18k"
    let label = rest;
    let investment = parseMoney(rest);
    const dashSplit = rest.split(/\s*[—–-]\s*/);
    if (dashSplit.length >= 2) {
      label = dashSplit[0].trim();
      investment = parseMoney(dashSplit.slice(1).join(" — ")) || investment;
    }
    const shortLabel = label || letter;
    const id = letter.toLowerCase();
    if (options.some((o) => o.id === id)) continue;
    options.push({
      id,
      letter,
      label: shortLabel,
      shortLabel,
      displayLabel: `Option ${letter} · ${shortLabel}`,
      investment,
    });
  }

  if (options.length < 2) return null;

  return {
    client_name,
    options,
  };
}
